import {
  canMarkLost,
  isLost,
  lostPatch,
  reopenPatch,
  type Job,
} from "@roof-hawk/core";
import { useState } from "react";

import { Modal } from "../../components/ui";
import { t } from "../../i18n";
import { fmtDateTime } from "../../lib/format";
import { useAsync } from "../../lib/hooks";
import { fetchLostReasons } from "../../lib/queries";
import { useSession } from "../../session";

/**
 * Marking a job lost, and taking it back.
 *
 * Losing a job never deletes it — the job keeps its contact, its estimate and
 * its money, and simply stops appearing on the boards. The reason is required,
 * because the reason is what the reporting is for, and it comes from the
 * managed list rather than being retyped.
 */
export function LostPanel({
  job,
  onPatchJob,
}: {
  job: Job;
  onPatchJob: (patch: Partial<Job>) => Promise<void>;
}) {
  const { profileId } = useSession();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const reasons = useAsync(fetchLostReasons, []);

  if (isLost(job)) {
    return (
      <div className="lostbar" role="status">
        <div>
          <b>
            {t("Lost — {reason}", {
              reason: job.lost_reason
                ? t(job.lost_reason)
                : t("no reason recorded"),
            })}
          </b>
          <div className="sub">
            {job.lost_at ? fmtDateTime(job.lost_at) : ""}
            {job.lost_note ? ` · ${job.lost_note}` : ""}
          </div>
        </div>
        <button
          className="btn-ghost"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void onPatchJob(reopenPatch() as Partial<Job>).finally(() =>
              setBusy(false),
            );
          }}
        >
          {t("Reopen job")}
        </button>
      </div>
    );
  }

  if (!canMarkLost(job)) return null;

  async function confirm() {
    if (!reason) return;
    setBusy(true);
    try {
      await onPatchJob(lostPatch({ reason, note }, profileId) as Partial<Job>);
      setAsking(false);
      setReason("");
      setNote("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button className="marklost" onClick={() => setAsking(true)}>
        {t("Mark job lost")}
      </button>

      {asking && (
        <Modal
          title={t("Mark this job lost")}
          subtitle={t(
            "Nothing is deleted. The job keeps its history and drops off the boards — you can reopen it later.",
          )}
          onClose={() => setAsking(false)}
          actions={
            <>
              <button className="btn-ghost" onClick={() => setAsking(false)}>
                {t("Cancel")}
              </button>
              <button
                className="btn-primary"
                disabled={!reason || busy}
                onClick={() => void confirm()}
              >
                {t("Mark lost")}
              </button>
            </>
          }
        >
          <div className="field full">
            <label htmlFor="lost-reason">{t("Reason")}</label>
            <select
              id="lost-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">{t("Pick a reason…")}</option>
              {(reasons.data ?? []).map((r) => (
                <option key={r.id} value={r.name}>
                  {t(r.name)}
                </option>
              ))}
            </select>
            {!reason && (
              <div className="na-hint">
                {t("Required — this is what the lost report counts.")}
              </div>
            )}
          </div>
          <div className="field full">
            <label htmlFor="lost-note">{t("Note (optional)")}</label>
            <input
              id="lost-note"
              value={note}
              placeholder={t("Anything worth remembering about why")}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
